import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus, Edit2, Trash2, X, AlertTriangle } from "lucide-react";
import Wrapper from "../../../components/Wrapper";
import { labApi } from "../../../services/labApi";

const getMinAlert = (c) => Number(c.min_stock_alert ?? c.minimum_stock_alert ?? 0);

const isLowStock = (c) => Number(c.current_stock || 0) <= getMinAlert(c);

const VendorCell = ({ vendors }) => {
  const [expanded, setExpanded] = useState(false);

  if (!vendors || vendors.length === 0) return <span>-</span>;

  const firstVendor = vendors[0];
  const hasMore = vendors.length > 1;

  return (
    <div className="space-y-1.5">
      <div className="leading-tight">
        <span className="font-medium text-slate-700">{firstVendor.name || "-"}</span>
        {(firstVendor.mobile || firstVendor.email) && (
          <span className="block text-[11px] text-slate-400">
            {firstVendor.mobile} {firstVendor.mobile && firstVendor.email && "•"} {firstVendor.email}
          </span>
        )}
      </div>
      {expanded && (
        <div className="space-y-2 pt-2 mt-2 border-t border-slate-100">
          {vendors.slice(1).map((v, i) => (
            <div key={i} className="leading-tight">
              <span className="font-medium text-slate-700">{v.name || "-"}</span>
              {(v.mobile || v.email) && (
                <span className="block text-[11px] text-slate-400">
                  {v.mobile} {v.mobile && v.email && "•"} {v.email}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[11px] text-emerald-600 hover:text-emerald-800 font-medium inline-block mt-1"
        >
          {expanded ? "Show Less" : `+ ${vendors.length - 1} more`}
        </button>
      )}
    </div>
  );
};

export default function RawChemicalsPage() {
  const [chemicals, setChemicals] = useState([]);
  const [search, setSearch] = useState("");
  const [showChemicalModal, setShowChemicalModal] = useState(false);
  const [editingChemical, setEditingChemical] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showViewBatchesModal, setShowViewBatchesModal] = useState(false);
  const [selectedChemical, setSelectedChemical] = useState(null);
  const [updatingBatchId, setUpdatingBatchId] = useState(null);
  
  // Added total_cost to form state
  const [chemicalForm, setChemicalForm] = useState({ 
    name: "", 
    unit: "kg", 
    current_stock: "", 
    minimum_stock_alert: "",
    total_cost: "" ,
    vendors: [{ name: "", mobile: "", email: "" }]
  });
  
  const [batchForm, setBatchForm] = useState({
    batch_code: "",
    quantity: "",
    received_date: new Date().toISOString().split("T")[0],
    expiry_date: "",
    supplier: "",
    unit_cost: "",
    total_cost: ""
  });

  const load = async () => {
    const rows = await labApi.getChemicals();
    setChemicals(rows);
    return rows;
  };

  useEffect(() => {
    load().catch(console.error);
  }, []);

  const filteredChemicals = useMemo(
    () => chemicals.filter((c) => c.name.toLowerCase().includes(search.toLowerCase())),
    [chemicals, search]
  );
  
  const sortedChemicals = useMemo(
    () =>
      [...filteredChemicals].sort((a, b) => {
        const stockDiff = Number(a.current_stock || 0) - Number(b.current_stock || 0);
        return stockDiff !== 0 ? stockDiff : a.name.localeCompare(b.name);
      }),
    [filteredChemicals]
  );
  
  const lowStockCount = useMemo(
    () => filteredChemicals.filter(isLowStock).length,
    [filteredChemicals]
  );

  const openChemicalModal = (chemical) => {
    setEditingChemical(chemical || null);
    setChemicalForm(
      chemical
        ? {
            name: chemical.name || "",
            unit: chemical.unit || "kg",
            current_stock: String(chemical.current_stock || ""),
            minimum_stock_alert: String(chemical.min_stock_alert || chemical.minimum_stock_alert || ""),
            total_cost: String(chemical.total_cost || ""),
            vendors: chemical.vendors?.length ? chemical.vendors : [{ name: "", mobile: "", email: "" }]
          }
        : { name: "", unit: "kg", current_stock: "", minimum_stock_alert: "", total_cost: "", vendors: [{ name: "", mobile: "", email: "" }] }
    );
    setShowChemicalModal(true);
  };

  const handleChemicalSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: chemicalForm.name,
      unit: chemicalForm.unit,
      current_stock: Number(chemicalForm.current_stock || 0),
      min_stock_alert: Number(chemicalForm.minimum_stock_alert || 0),
      total_cost: Number(chemicalForm.total_cost || 0),
      vendors: chemicalForm.vendors
    };
    if (editingChemical) {
      await labApi.updateChemical(editingChemical.id, payload);
    } else {
      await labApi.createChemical(payload);
    }
    setShowChemicalModal(false);
    await load();
  };

  const handleDeleteChemical = async (id) => {
    await labApi.deleteChemical(id);
    await load();
  };

  const openBatchModal = (chemical) => {
    setSelectedChemical(chemical);
    const defaultVendor = chemical.vendors && chemical.vendors.length > 0 ? chemical.vendors[0].name : "";

    setBatchForm({
      batch_code: `${chemical.name.slice(0, 3).toUpperCase()}-${Date.now().toString().slice(-6)}`,
      quantity: "",
      received_date: new Date().toISOString().split("T")[0],
      expiry_date: "",
      supplier: defaultVendor,
      unit_cost: "",
      total_cost: ""
    });
    setShowBatchModal(true);
  };

  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    if (!selectedChemical) return;
    await labApi.addChemicalBatch(selectedChemical.id, {
      batch_code: batchForm.batch_code,
      quantity: Number(batchForm.quantity || 0),
      received_date: batchForm.received_date,
      expiry_date: batchForm.expiry_date || null,
      supplier: batchForm.supplier || null,
      total_cost: Number(batchForm.total_cost || 0)
    });
    setShowBatchModal(false);
    await load();
  };

  const openViewBatchesModal = (chemical) => {
    setSelectedChemical(chemical);
    setShowViewBatchesModal(true);
  };

  const updateBatchStatus = async (chemicalId, batchId, status) => {
    try {
      setUpdatingBatchId(batchId);
      await labApi.updateChemicalBatchStatus(chemicalId, batchId, status);
      const rows = await load();
      const updatedChemical = rows.find((c) => c.id === chemicalId);
      if (updatedChemical) {
        setSelectedChemical(updatedChemical);
      }
    } finally {
      setUpdatingBatchId(null);
    }
  };

  const handleVendorChange = (index, field, value) => {
    const newVendors = [...chemicalForm.vendors];
    newVendors[index][field] = value;
    setChemicalForm({ ...chemicalForm, vendors: newVendors });
  };

  const addVendor = () => {
    setChemicalForm({ 
      ...chemicalForm, 
      vendors: [...chemicalForm.vendors, { name: "", mobile: "", email: "" }] 
    });
  };

  const removeVendor = (index) => {
    const newVendors = chemicalForm.vendors.filter((_, i) => i !== index);
    setChemicalForm({ ...chemicalForm, vendors: newVendors });
  };

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="w-full text-left">
            <h2 className="m-0 text-2xl font-bold text-slate-800 text-left">Raw Chemicals</h2>
            <p className="mt-1 text-slate-500 text-left">Live chemicals and batch data</p>
          </div>
          <button onClick={() => openChemicalModal(null)} className="flex items-center gap-2 self-start bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700">
            <Plus size={18} />
            Add Chemical
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search chemicals..." className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg" />
        </div>

        {lowStockCount > 0 ? (
          <div className="flex items-center gap-2 text-sm text-red-700">
            <AlertTriangle size={16} />
            <span>{lowStockCount} chemical{lowStockCount === 1 ? "" : "s"} at or below minimum alert</span>
          </div>
        ) : null}

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2 text-left font-medium w-12">S.No</th>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-left font-medium">Vendors</th>
                <th className="px-3 py-2 text-left font-medium">Unit</th>
                <th className="px-3 py-2 text-right font-medium">Current Stock</th>
                <th className="px-3 py-2 text-right font-medium">Min Alert</th>
                <th className="px-3 py-2 text-right font-medium">Total Value</th>
                <th className="px-3 py-2 text-right font-medium">Batches</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedChemicals.map((c, index) => {
                const low = isLowStock(c);
                return (
                <tr key={c.id} className={low ? "bg-red-50 hover:bg-red-100/60" : "hover:bg-slate-50"}>
                  <td className="px-3 py-2 text-slate-500">{index + 1}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">{c.name}</td>
                  <td className="px-3 py-2 text-slate-600">
                    <VendorCell vendors={c.vendors} />
                  </td>
                  <td className="px-3 py-2 text-slate-500">{c.unit}</td>
                  <td className={`px-3 py-2 text-right font-semibold tabular-nums ${low ? "text-red-700" : "text-slate-800"}`}>
                    {c.current_stock}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-500 tabular-nums">{getMinAlert(c)}</td>
                  <td className="px-3 py-2 text-right font-medium text-emerald-700">
                    ₹{Number(c.total_cost || 0).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => openViewBatchesModal(c)} className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-sm">
                        View ({(c.batches || []).length})
                      </button>
                      <button onClick={() => openBatchModal(c)} className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-sm">
                        Add Batch
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openChemicalModal(c)} className="p-2 hover:bg-slate-100 rounded-lg"><Edit2 size={16} /></button>
                      <button onClick={() => handleDeleteChemical(c.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
          {sortedChemicals.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-slate-500">No chemicals found</p>
          ) : null}
        </div>

        {/* --- ADD/EDIT CHEMICAL MODAL --- */}
        {showChemicalModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">{editingChemical ? "Edit Chemical" : "Add Chemical"}</h3>
                <button onClick={() => setShowChemicalModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
              </div>
              <form onSubmit={handleChemicalSubmit} className="p-4 space-y-3">
                <input value={chemicalForm.name} onChange={(e) => setChemicalForm({ ...chemicalForm, name: e.target.value })} placeholder="Name" className="w-full px-3 py-2 border rounded-lg" required />
                
                <div className="grid grid-cols-2 gap-3">
                  <input value={chemicalForm.unit} onChange={(e) => setChemicalForm({ ...chemicalForm, unit: e.target.value })} placeholder="Unit" className="w-full px-3 py-2 border rounded-lg" required />
                  <input value={chemicalForm.minimum_stock_alert} onChange={(e) => setChemicalForm({ ...chemicalForm, minimum_stock_alert: e.target.value })} type="number" placeholder="Min Stock Alert" className="w-full px-3 py-2 border rounded-lg" />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Current Stock</label>
                    <input value={chemicalForm.current_stock} onChange={(e) => setChemicalForm({ ...chemicalForm, current_stock: e.target.value })} type="number" placeholder="0" className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Total Value (₹)</label>
                    <input value={chemicalForm.total_cost} onChange={(e) => setChemicalForm({ ...chemicalForm, total_cost: e.target.value })} type="number" step="0.01" placeholder="0.00" className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                </div>

                <div className="space-y-3 border border-slate-200 rounded-xl p-4 bg-slate-50">
                  <div className="flex justify-between items-center">
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Vendors
                    </label>
                    <button 
                      type="button" 
                      onClick={addVendor}
                      className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-lg"
                    >
                      <Plus size={16} /> Add Vendor
                    </button>
                  </div>
                  
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                    {chemicalForm.vendors.map((vendor, index) => (
                      <div key={index} className="p-3 bg-white border border-slate-200 rounded-lg relative space-y-3 shadow-sm">
                        <div className="flex gap-2 items-center">
                          <input 
                            value={vendor.name} 
                            onChange={(e) => handleVendorChange(index, 'name', e.target.value)} 
                            placeholder="Vendor Name" 
                            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-500" 
                            required 
                          />
                          <button 
                            type="button" 
                            onClick={() => removeVendor(index)}
                            disabled={chemicalForm.vendors.length === 1}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 rounded-lg transition-colors"
                            title="Remove Vendor"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <input 
                            value={vendor.mobile} 
                            onChange={(e) => handleVendorChange(index, 'mobile', e.target.value)} 
                            placeholder="Mobile / Landline" 
                            maxLength={11}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-500" 
                          />
                          <input 
                            value={vendor.email} 
                            onChange={(e) => handleVendorChange(index, 'email', e.target.value)} 
                            placeholder="Email Address" 
                            type="email"
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-500" 
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setShowChemicalModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* --- ADD BATCH MODAL --- */}
        {showBatchModal && selectedChemical && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-md p-4">
              <h3 className="text-lg font-semibold mb-3">Add Batch: {selectedChemical.name}</h3>
              <form onSubmit={handleBatchSubmit} className="space-y-3">
                <input 
                  value={batchForm.batch_code} 
                  onChange={(e) => setBatchForm({ ...batchForm, batch_code: e.target.value })} 
                  placeholder="Batch Code" 
                  className="w-full px-3 py-2 border rounded-lg" 
                  required 
                />
                
                <div className="grid grid-cols-2 gap-3">
                  <input 
                    value={batchForm.quantity} 
                    onChange={(e) => {
                      const qty = e.target.value;
                      setBatchForm(prev => ({
                        ...prev, 
                        quantity: qty,
                        total_cost: prev.unit_cost && qty ? (Number(prev.unit_cost) * Number(qty)).toFixed(2) : prev.total_cost
                      }));
                    }} 
                    type="number" 
                    placeholder="Quantity" 
                    className="w-full px-3 py-2 border rounded-lg" 
                    required 
                  />
                  <input 
                    value={batchForm.received_date} 
                    onChange={(e) => setBatchForm({ ...batchForm, received_date: e.target.value })} 
                    type="date" 
                    className="w-full px-3 py-2 border rounded-lg" 
                    required 
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Expiry Date</label>
                    <input 
                      value={batchForm.expiry_date} 
                      onChange={(e) => setBatchForm({ ...batchForm, expiry_date: e.target.value })} 
                      type="date" 
                      className="w-full px-3 py-2 border rounded-lg" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Received From (Vendor)</label>
                    <select 
                      value={batchForm.supplier} 
                      onChange={(e) => setBatchForm({ ...batchForm, supplier: e.target.value })} 
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:border-emerald-500" 
                    >
                      <option value="">-- Select Vendor --</option>
                      {(selectedChemical.vendors || []).map((v, idx) => {
                        if (!v.name) return null;
                        return (
                          <option key={idx} value={v.name}>{v.name}</option>
                        )
                      })}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Unit Cost (Optional)</label>
                    <input 
                      value={batchForm.unit_cost} 
                      onChange={(e) => {
                        const uc = e.target.value;
                        setBatchForm(prev => ({
                          ...prev, 
                          unit_cost: uc,
                          total_cost: prev.quantity && uc ? (Number(prev.quantity) * Number(uc)).toFixed(2) : prev.total_cost
                        }));
                      }} 
                      type="number" 
                      step="0.01"
                      placeholder="e.g. 5.50" 
                      className="w-full px-3 py-2 border rounded-lg" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Total Batch Cost</label>
                    <input 
                      value={batchForm.total_cost} 
                      onChange={(e) => {
                        const tc = e.target.value;
                        setBatchForm(prev => ({
                          ...prev, 
                          total_cost: tc,
                          unit_cost: prev.quantity && tc ? (Number(tc) / Number(prev.quantity)).toFixed(4) : prev.unit_cost
                        }));
                      }} 
                      type="number" 
                      step="0.01"
                      placeholder="e.g. 250.00" 
                      className="w-full px-3 py-2 border rounded-lg" 
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={() => setShowBatchModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg">Submit</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* --- VIEW BATCHES MODAL --- */}
        {showViewBatchesModal && selectedChemical && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">
                  Batches: {selectedChemical.name}
                </h3>
                <button onClick={() => setShowViewBatchesModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>
              <div className="p-4 max-h-[60vh] overflow-y-auto">
                {(selectedChemical.batches || []).length === 0 ? (
                  <p className="text-sm text-slate-500">No batches found for this chemical.</p>
                ) : (
                  <div className="space-y-2">
                    {(selectedChemical.batches || []).map((batch) => {
                      const status = String(batch.status || "approved").toLowerCase();
                      const batchActionsLocked = updatingBatchId === batch.id || status === "approved" || status === "rejected";
                      
                      // 7-day calculation logic
                      const receivedDate = new Date(batch.received_date);
                      const now = new Date();
                      const diffDays = (now - receivedDate) / (1000 * 60 * 60 * 24);
                      const isWithin7Days = diffDays <= 7;

                      return (
                        <div key={batch.id} className="border border-slate-200 rounded-lg p-3">
                          <p className="m-0 font-medium text-slate-800">{batch.batch_code || `Batch #${batch.id}`}</p>
                          <p className="m-0 text-sm text-slate-600">
                            Qty: {batch.quantity} | Status: <span className="capitalize">{status}</span>
                            {batch.total_cost > 0 && ` | Cost: ₹${Number(batch.total_cost).toFixed(2)}`}
                          </p>
                          
                          <p className="m-0 text-xs text-slate-500 mt-1">
                            Received: {batch.received_date ? String(batch.received_date).split("T")[0] : "-"} 
                            {batch.expiry_date && ` | Expiry: ${String(batch.expiry_date).split("T")[0]}`}
                            {batch.supplier && ` • Vendor: ${batch.supplier}`}
                          </p>

                          {/* Use updated_at for the return date */}
                          {status === "returned" && batch.updated_at && (
                            <p className="m-0 text-xs text-red-500 mt-0.5">
                              Returned on: {String(batch.updated_at).split("T")[0]}
                            </p>
                          )}
                          
                          <div className="mt-3 flex gap-2">
                            {status === "returned" ? (
                              <span className="px-3 py-1 bg-slate-100 text-slate-500 text-xs rounded-lg font-medium">
                                Returned
                              </span>
                            ) : isWithin7Days ? (
                              <button
                                type="button"
                                disabled={updatingBatchId === batch.id}
                                onClick={() => updateBatchStatus(selectedChemical.id, batch.id, "returned")}
                                className="px-3 py-1 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 text-xs font-medium transition-colors disabled:opacity-50"
                              >
                                Return Batch
                              </button>
                            ) : (
                              <span className="text-xs text-slate-400 italic">Return window expired</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="p-4 border-t">
                <button type="button" onClick={() => setShowViewBatchesModal(false)} className="px-4 py-2 border rounded-lg">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}